import {
  Autocomplete,
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Paper,
  alpha,
  Divider,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { getFacilityByUserId, getPayments, getPaymentSummary } from '../../../../helpers/api';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';
import {
  AttachFile,
  Comment,
  HistoryOutlined,
  BusinessRounded,
  AccountBalanceWalletOutlined,
  CheckCircleOutline,
  ErrorOutline,
  FilterList,
} from '@mui/icons-material';
import PaymentComment from '../payment-actions/PaymentComment';
import AppPagination from '../../../common/AppPagination';
import { getYears } from '../../../../helpers/dateHelpers';

const PaymentApproved = withRouter(({ props }) => {
  const { userCategory, userPermission, userId, location, history } = props;
  const searchParams = new URLSearchParams(location?.search);

  // Core Params
  const status = searchParams.get('status') || '';
  const facilityId = searchParams.get('facilityId') || '';
  const year = searchParams.get('year') || 'all';
  const referenceNo = searchParams.get('referenceNo') || '';
  const invoiceId = searchParams.get('invoiceId') || '';
  const paymentMethod = searchParams.get('paymentMethod') || '';
  const paymentGateWay = searchParams.get('paymentGateWay') || '';

  const years = getYears();
  const initFacility = { facilityId: '', facilityName: '' };

  // Component States
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const pageSize = 10;

  const [commentSelected, setCommentSelected] = useState('');
  const [openComment, setOpenComment] = useState(false);

  const [facilities, setFacilities] = useState([]);
  const [facilitySelected, setFailitySelected] = useState(initFacility);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState();

  // --- Logic Effects ---
  useEffect(() => {
    handleInitialYear();
    fetchFacilities();
  }, []);

  useEffect(() => {
    handleInitialFacility();
  }, [facilities]);

  useEffect(() => {
    fetchPaymentSumary();
  }, [year, facilityId]);

  useEffect(() => {
    fetchPayments();
  }, [facilityId, referenceNo, invoiceId, status, year, paymentMethod, paymentGateWay, pageNum]);

  // --- Core Functionalities (Retained) ---
  const handleInitialFacility = () => {
    if (facilityId) {
      const findFacility = facilities?.find((x) => x.facilityId === facilityId);
      setFailitySelected(findFacility || initFacility);
    }
  };

  const handleInitialYear = () => {
    if (!year || year === 'all') {
      const newParam = new URLSearchParams(searchParams);
      newParam.set('year', new Date().getFullYear());
      history.push({ search: newParam.toString() });
    }
  };

  const handleSelectYear = (val) => {
    const newParam = new URLSearchParams(searchParams);
    newParam.set('year', val);
    history.push({ search: newParam.toString() });
  };

  const handleSelectFacility = (facility) => {
    const newParam = new URLSearchParams(searchParams);
    if (facility) newParam.set('facilityId', facility?.facilityId);
    else newParam.delete('facilityId');
    history.push({ search: newParam.toString() });
  };

  const fetchFacilities = () => {
    setLoadingFacilities(true);
    getFacilityByUserId(userId)
      .then((response) => setFacilities(response?.data || []))
      .catch((err) => console.error(err))
      .finally(() => setLoadingFacilities(false));
  };

  const fetchPaymentSumary = () => {
    getPaymentSummary(year, '', facilityId)
      .then((res) => setPaymentSummary(res?.result?.data))
      .catch((error) => console.error(error));
  };

  const fetchPayments = () => {
    setLoading(true);
    getPayments(
      facilityId,
      referenceNo,
      invoiceId,
      status,
      year === 'all' ? '' : year,
      paymentMethod,
      paymentGateWay,
      pageNum,
      pageSize
    )
      .then((res) => {
        setPayments(res?.result?.data || []);
        setTotal(res?.result?.total || 0);
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  };

  // --- Content Rendering logic (Restored & Enhanced) ---
  const handleContentRendering = () => {
    if (loading) {
      return (
        <Box sx={{ p: 4 }}>
          <LoaderSkeleton />
          <Divider sx={{ my: 3 }} />
          <LoaderSkeleton />
        </Box>
      );
    }
    if (payments?.length < 1) {
      return (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Empty description="No confirmed payment records found" />
        </Box>
      );
    }
    return renderContent();
  };

  const renderContent = () => {
    const columns = [
      {
        name: 'paymentDate',
        label: 'Payment Date',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  bgcolor: alpha('#1976d2', 0.1),
                  color: 'primary.main',
                  display: 'flex',
                }}
              >
                <HistoryOutlined sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {new Date(payments[index]?.paymentDate).toLocaleDateString('en-NG', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Typography>
            </Stack>
          ),
        },
      },
      {
        name: 'amountPaid',
        label: 'Amount Paid',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={700} color="success.main">
                {Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(
                  payments[index]?.amountPaid
                )}
              </Typography>
              {payments[index]?.paymentReceipt && (
                <Tooltip title="View Receipt">
                  <IconButton
                    size="small"
                    onClick={() => window.open(payments[index].paymentReceipt)}
                  >
                    <AttachFile sx={{ fontSize: 16 }} color="success" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ),
        },
      },
      {
        name: 'facilityName',
        label: 'Facility Details',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Stack direction="row" spacing={1} alignItems="center">
              <BusinessRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                {payments[index]?.facilityName}
              </Typography>
            </Stack>
          ),
        },
      },
      {
        name: 'verificationStatus',
        label: 'Status',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const status = payments[index]?.verificationStatus;
            const isApproved = status === 'APPROVED';
            return (
              <Chip
                label={isApproved ? 'Approved' : 'Pending'}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  bgcolor: isApproved ? alpha('#2e7d32', 0.1) : alpha('#ed6c02', 0.1),
                  color: isApproved ? '#2e7d32' : '#ed6c02',
                  borderRadius: '6px',
                }}
              />
            );
          },
        },
      },
      {
        name: 'approver',
        label: 'Approver / Remarks',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const comment = payments[index]?.approverComment;
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {payments[index]?.approver || 'N/A'}
                </Typography>
                {comment && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setCommentSelected(comment);
                      setOpenComment(true);
                    }}
                  >
                    <Comment color="info" sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Stack>
            );
          },
        },
      },
    ];

    return (
      <MUIDataTable
        data={payments}
        columns={columns}
        options={{
          elevation: 0,
          selectableRows: 'none',
          responsive: 'standard',
          pagination: false,
          setRowProps: (ev, dataIndex) => ({
            style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
          }),
          setTableProps: () => ({ size: 'small' }),
        }}
      />
    );
  };

  return (
    <Box>
      {/* FILTER PANEL */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha('#F4F6F8', 0.5),
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
          <Autocomplete
            options={facilities}
            getOptionLabel={(option) => option.facilityName || ''}
            value={facilitySelected.facilityId ? facilitySelected : null}
            onChange={(e, value) => handleSelectFacility(value)}
            size="small"
            sx={{ width: { xs: '100%', sm: 250 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Filter by facility"
                InputProps={{
                  ...params.InputProps,
                  sx: { borderRadius: '10px', bgcolor: '#fff' },
                  startAdornment: (
                    <>
                      {loadingFacilities && <CircularProgress size={16} sx={{ mr: 1 }} />}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Select
            value={year}
            size="small"
            onChange={(e) => handleSelectYear(e.target.value)}
            startAdornment={<FilterList sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />}
            sx={{ borderRadius: '10px', width: { xs: '100%', sm: 150 }, bgcolor: '#fff' }}
          >
            {years?.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Paper>

      {/* METRIC SUMMARY CARDS */}
      {paymentSummary && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
          {[
            {
              label: 'Total Expected',
              val: paymentSummary.totalAmount,
              color: '#ed6c02',
              icon: <AccountBalanceWalletOutlined />,
            },
            {
              label: 'Total Paid',
              val: paymentSummary.totalAmountPaid,
              color: '#2e7d32',
              icon: <CheckCircleOutline />,
            },
            {
              label: 'Total Outstanding',
              val: paymentSummary.totalOutstandingAmount,
              color: '#d32f2f',
              icon: <ErrorOutline />,
            },
          ].map((card, i) => (
            <Card
              key={i}
              elevation={0}
              sx={{
                flex: 1,
                p: 2.5,
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: card.color, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {card.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                    {Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(
                      card.val
                    )}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    bgcolor: alpha(card.color, 0.1),
                    color: card.color,
                    display: 'flex',
                  }}
                >
                  {card.icon}
                </Box>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {/* TABLE AREA */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {handleContentRendering()}
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <AppPagination defaultCurrent={1} total={total} onPageChange={(p) => setPageNum(p)} />
      </Box>

      {/* MODALS */}
      {openComment && commentSelected && (
        <PaymentComment
          open={openComment}
          onClose={() => {
            setOpenComment(false);
            setCommentSelected('');
          }}
          comment={commentSelected}
        />
      )}
    </Box>
  );
});

export default PaymentApproved;
